import React, { useState } from "react";
import { useTaskFlow } from "../ai-task-button";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { CalendarPlusIcon, XIcon, ExternalLinkIcon, UserPlusIcon } from "lucide-react";
import { UserDropdown } from "../../../../components/shared/org/user-dropdown";
import { PopoverTrigger } from "../../../../components/ui/popover";
import { AvatarForUser } from "../../../../components/shared/org/avatar-for-user";
import { useCurrentOrgUser, useOrg } from "../../../../auth/org-provider";
import { useScheduleEvent } from "../../../../lib/event-scheduling";
import { Converter } from "showdown";
import { useProject } from "../../../../data/project";
import { MeetingData, ScheduleMeetingResponse } from "../../../../data/types/ai-task-types";

interface ScheduleMeetingFlowProps {
    data: ScheduleMeetingResponse;
}

export function ScheduleMeetingFlow({ data }: ScheduleMeetingFlowProps) {
    const { setTaskComplete, setCancel } = useTaskFlow();
    const { project } = useProject();

    const handleSuccess = () => {
        setTaskComplete();
    };

    const handleCancel = () => {
        setCancel();
    };

    return (
        <ScheduleMeetingDialog
            meetingData={data.data}
            projectId={project.id}
            suggestedUserIds={data.data.suggestedParticipantsUserIds}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
        />
    );
}

interface ScheduleMeetingDialogProps {
    meetingData: MeetingData;
    projectId: string;
    suggestedUserIds: string[];
    onSuccess: () => void;
    onCancel: () => void;
}

function ScheduleMeetingDialog({
    meetingData,
    projectId,
    suggestedUserIds,
    onSuccess,
    onCancel,
}: ScheduleMeetingDialogProps) {
    const org = useOrg();
    const currentUser = useCurrentOrgUser();
    const scheduleEvent = useScheduleEvent();
    const suggestedUsersEmails = suggestedUserIds
        .map((userId) => org.users.find((u) => u.id === userId)?.email)
        .filter(Boolean) as string[];

    const [invitees, setInvitees] = useState<string[]>(() =>
        Array.from(new Set([currentUser.email, ...suggestedUsersEmails]))
    );

    const addInvitee = (email: string) => {
        setInvitees((prev) => (prev.includes(email) ? prev : [...prev, email]));
    };

    const removeInvitee = (email: string) => {
        setInvitees((prev) => prev.filter((e) => e !== email));
    };

    const handleConfirm = async () => {
        const converter = new Converter();
        await scheduleEvent(org.officeSuiteProvider, {
            eventName: meetingData.title,
            description: converter.makeHtml(meetingData.description),
            meetingDuration: { minutes: meetingData.duration },
            invitees,
            context: { projectId, scope: ["project", "task"] },
        });
        onSuccess();
    };

    return (
        <Dialog open={true} onOpenChange={() => onCancel()}>
            <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
                <DialogTitle className="flex flex-row items-center">
                    <CalendarPlusIcon className="me-2 h-5 w-5" />
                    Schedule Meeting
                </DialogTitle>

                <div className="-mx-6 flex flex-1 flex-col gap-6 overflow-y-auto px-6">
                    <div className="flex flex-col gap-3">
                        <p className="pt-4 text-sm font-medium uppercase tracking-wider text-primary">
                            Suggested Invitees
                        </p>

                        <div className="flex flex-col gap-2">
                            {invitees.map((email) => {
                                const user = org.users.find((u) => u.email === email);
                                return (
                                    <div
                                        key={email}
                                        className="flex items-center justify-between gap-2 rounded-md border bg-background p-2"
                                    >
                                        <div className="flex items-center gap-2">
                                            <AvatarForUser
                                                user={{
                                                    id: user?.id || "",
                                                    name: user?.name || email,
                                                    imageUrl: user?.imageUrl,
                                                }}
                                                className="h-8 w-8"
                                                fallback="initials"
                                            />
                                            <div className="flex flex-col">
                                                <span className="text-sm">{user?.name || email}</span>
                                                {user?.name && (
                                                    <span className="text-xs text-muted-foreground">{email}</span>
                                                )}
                                            </div>
                                        </div>
                                        {email !== currentUser.email && (
                                            <Button
                                                onClick={() => removeInvitee(email)}
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 w-6 p-0"
                                            >
                                                <XIcon className="h-4 w-4 text-muted-foreground" />
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-end">
                            <UserDropdown
                                users={org.users}
                                onSelect={(userId) => {
                                    const user = org.users.find((u) => u.id === userId);
                                    if (user?.email) {
                                        addInvitee(user.email);
                                    }
                                }}
                            >
                                <PopoverTrigger>
                                    <Button variant="outline-primary" size="sm" className="gap-2">
                                        <UserPlusIcon className="h-4 w-4" />
                                        Add participant
                                    </Button>
                                </PopoverTrigger>
                            </UserDropdown>
                        </div>
                    </div>
                </div>

                <div className="rounded-md bg-muted/50 p-3">
                    <div className="flex items-end justify-center gap-2">
                        <p className="text-sm text-muted-foreground">
                            Don't worry - you'll be able to edit the meeting details, participants and attach document
                            in {org.officeSuiteProvider === "google" ? "Google calendar" : "Outlook"} in the next step.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="secondary" onClick={() => onCancel()}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} className="min-w-[150px]">
                        <ExternalLinkIcon className="me-2 h-4 w-4" />
                        Go to calendar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
